'use strict';

/**
 * @ngdoc service
 * @name vTvPackageServices.TvPackageSelectionStateService
 * @description Preserves the state of the cart selection within the current user session
 */

/* eslint-disable max-statements */
angular.module('v-tv-package-services').service('TvPackageSelectionStateService', function TvPackageSelectionStateService(TvPackageSelectionUpdateService, TvPackageCartFactory, $rootScope, $q) {
    var _this = this;

    var selectedPackageId = null;
    var domainName = 'residential';
    var stores = [];
    var activeCart = null;
    var hasCartChanged = false;
    /* eslint-disable no-unused-vars */
    var authenticatedMode = false;
    /* eslint-enable no-unused-vars */
    var aLaCarteMode = false;
    var canSelectALaCarte = true;
    var aLaCarteCart = null;
    var packageCart = null;
    var categoryAvailability = null;

    /**
     * @ngdoc method
     * @private
     * @name vTvPackageServices.TvPackageSelectionStateService#addCart
     * @methodOf vTvPackageServices.TvPackageSelectionStateService
     * @param {vTvPackageServices.TvPackageCart} cart A cart instance that this service should track
     * @param {boolean} isALaCarte set to true if this cart represents the a la carte mode
     */

    function addCart(cart, isALaCarte) {
        if (isALaCarte) {
            aLaCarteCart = cart;
        } else {
            packageCart = cart;
        }
    }

    addCart(new TvPackageCartFactory('aLaCarte'), true);
    addCart(new TvPackageCartFactory('package'));

    /**
     * @ngdoc method
     * @private
     * @name vTvPackageServices.TvPackageSelectionStateService#getCartStoreKey
     * @methodOf vTvPackageServices.TvPackageSelectionStateService
     * @param {vTvPackageServices.TvPackageCart} cart A cart instance that this service should track
     * @returns {string} The key used to store the current cart
     */
    function getCartStoreKey(cart) {
        return cart.getKey();
    }

    function loadCartFromStore(cart) {
        var storeKey = getCartStoreKey(cart);

        function getFromStoreIndex(index) {
            return stores[index].getItem(storeKey).then(function (value) {
                if (value === null && index < stores.length - 1) {
                    return getFromStoreIndex(index + 1);
                }

                return value;
            });
        }

        return getFromStoreIndex(0);
    }

    /**
     * @ngdoc method
     * @private
     * @description preserves a cart into the available stores
     * @name vTvPackageServices.TvPackageSelectionStateService#saveCartInStore
     * @methodOf vTvPackageServices.TvPackageSelectionStateService
     * @param {vTvPackageServices.TvPackageCart} cart A cart instance that this service should track
     * @returns {Promise} A Promise that completes once all the stores are done saving the cart
     */

    function saveCartInStore(cart) {
        var dataProms = [];
        var storeKey = getCartStoreKey(cart);

        angular.forEach(stores, function (store) {
            if (store.canWrite) {
                dataProms.push(store.setItem(storeKey, cart.getCartData()));
            }
        });

        return $q.all(dataProms);
    }

    /**
     * @ngdoc method
     * @description Saves the currently active cart to defined stores
     * @name vTvPackageServices.TvPackageSelectionStateService#saveCartInStore
     * @methodOf vTvPackageServices.TvPackageSelectionStateService
     * @returns {Promise} A Promise that completes once all the stores are done saving the cart
     */
    this.saveActiveCart = function () {
        if (!activeCart) {
            throw new Error('saveActiveCart: needs an active cart to save!');
        }

        return saveCartInStore(activeCart);
    };

    /**
     * @ngdoc method
     * @private
     * @name vTvPackageServices.TvPackageCartFactory#setCategoryAvailability
     * @methodOf vTvPackageServices.TvPackageCartFactory
     * @param {object} cartState The state
     * @returns {object} categoryAvailability with validity flags based on cartState (response from process-selection api)
     */

    function setCategoryAvailability(cartState) {
        categoryAvailability = {};

        for (var i = 0, lengthPool = cartState.selection.channelsByCategory.length; i < lengthPool; i++) {
            var channelsCategories = cartState.selection.channelsByCategory[i];

            for (var j = 0, lengthCategories = channelsCategories.categoryIds.length; j < lengthCategories; j++) {
                categoryAvailability[channelsCategories.categoryIds[j]] = !(channelsCategories.rules.isLimited && channelsCategories.rules.limit === 0);
            }
        }

        return categoryAvailability;
    }

    /**
     * @ngdoc method
     * @name vTvPackageServices.TvPackageSelectionStateService#setDomain
     * @methodOf vTvPackageServices.TvPackageSelectionStateService
     * @param {string} domain The domain were the packages must comes from: "residential" or "business"
     */

    this.setDomain = function (domain) {
        domainName = domain || 'residential';
        hasCartChanged = true;
    };

    /**
     * @ngdoc method
     * @name vTvPackageServices.TvPackageSelectionStateService#setALaCarte
     * @methodOf vTvPackageServices.TvPackageSelectionStateService
     * @param {boolean} isALaCarte setter if provided. True if the a la carte mode should be displayed. Otherwise getter
     * @return {Promise} Returns the cart once it had been initialized.
     */
    this.setALaCarte = function (isALaCarte) {
        aLaCarteMode = isALaCarte && canSelectALaCarte;
        return setActiveCart(aLaCarteMode ? aLaCarteCart : packageCart);
    };

    /**
     * @ngdoc method
     * @name vTvPackageServices.TvPackageSelectionStateService#isALaCarte
     * @methodOf vTvPackageServices.TvPackageSelectionStateService
     * @return {boolean} Returns true if the mode is a la carte.
     */
    this.isALaCarte = function () {
        return aLaCarteMode;
    };

    /**
     * @ngdoc method
     * @name vTvPackageServices.TvPackageSelectionStateService#canSelectALaCarte
     * @methodOf vTvPackageServices.TvPackageSelectionStateService
     * @param {boolean} canSelect defines if the a la carte mode is available
     * @description getter/setter  if the parameter is provided, sets whether a la carte mode is available or not.
     * @returns {boolean} the current state of the availability of à la carte mode.
     */
    this.canSelectALaCarte = function (canSelect) {
        if (angular.isDefined(canSelect)) {
            canSelectALaCarte = canSelect;
        }

        return canSelectALaCarte;
    };

    /**
     * @ngdoc method
     * @name vTvPackageServices.TvPackageSelectionStateService#setAuthencatedMode
     * @methodOf vTvPackageServices.TvPackageSelectionStateService
     * @param {boolean} useAuthentication if we want tv package to behave using authentication
     */

    /* eslint-disable no-unused-vars */
    this.setAuthenticatedMode = function (useAuthentication) {
        authenticatedMode = useAuthentication;
    };
    /* eslint-enable no-unused-vars*/

    /**
     * @ngdoc method
     * @name vTvPackageServices.TvPackageSelectionStateService#addStore
     * @methodOf vTvPackageServices.TvPackageSelectionStateService
     * @param {vTvPackageServices.TvPackageCartStore} store A store that we should use
     */

    this.addStore = function (store) {
        stores.push(store);
    };

    /**
     * @ngdoc method
     * @name vTvPackageServices.TvPackageSelectionStateService#setSelectedPackageId
     * @methodOf vTvPackageServices.TvPackageSelectionStateService
     * @param {number} packageId sets the id of the package that is currently selected
     */

    this.setSelectedPackageId = function (packageId) {
        selectedPackageId = packageId;
        hasCartChanged = true;
    };

    /**
     * @ngdoc method
     * @private
     * @name vTvPackageServices.TvPackageSelectionStateService#setActiveCart
     * @methodOf vTvPackageServices.TvPackageSelectionStateService
     * @param {vTvPackageServices.TvPackageCart} cart A cart instance that is currently active
     * @description sets the active cart. If cart is not initialized, Cart Stores will be called to initialise it.
     * @returns {Promise|vTvPackageServices.TvPackageCart} the active cart
     */
    function setActiveCart(cart) {
        if (activeCart !== null && cart !== activeCart && hasCartChanged) {
            throw new Error('Cannot change active cart while changes are not processed! ' + 'Use TvPackageSelectionStateService.updateActiveCart first or check TvPackageSelectionStateService.hasChanged()' + 'before calling this function');
        }

        activeCart = cart;

        if (!cart.isInitialized()) {
            return loadCartFromStore(cart).then(function (cartData) {
                if (cartData && 'selectedChannels' in cartData && cartData.selectedChannels.length) {
                    cart.setIsDisabled(false);
                }
                cart.setCartData(cartData);
                return cart;
            });
        }

        return $q.when(cart);
    }

    /**
     * @ngdoc method
     * @name vTvPackageServices.TvPackageSelectionStateService#getActiveCart
     * @methodOf vTvPackageServices.TvPackageSelectionStateService
     * @returns {vTvPackageServices.TvPackageCart} the instance of the currently active cart
     */
    this.getActiveCart = function () {
        return activeCart;
    };

    /**
     * @ngdoc method
     * @name vTvPackageServices.TvPackageSelectionStateService#updateActiveCart
     * @methodOf vTvPackageServices.TvPackageSelectionStateService
     * @returns {Promise} A promise with the instance of the currently active cart once the update is completed
     */
    this.updateActiveCart = function () {
        return _this.getSelectionState().then(function (response) {
            var cart = _this.getActiveCart();

            cart.setCartData(response.data.channelSelection);
            hasCartChanged = false;

            return _this.getActiveCart();
        });
    };

    /**
     * @ngdoc method
     * @name vTvPackageServices.TvPackageSelectionStateService#getSelectionState
     * @methodOf vTvPackageServices.TvPackageSelectionStateService
     * @returns {Promise} A promise with the result of the process-selection api call
     */
    this.getSelectionState = function () {
        return _this.setALaCarte(aLaCarteMode).then(function (cart) {
            return TvPackageSelectionUpdateService.processCart(cart, _this.isALaCarte()).then(function (response) {
                if (!cart.getIsDisabled()) {
                    setCategoryAvailability(response.data);
                    $rootScope.$broadcast('TvPackageSelectionStateService.selectionProcessed', response.data);
                }

                return response.data;
            });
        });
    };

    /**
     * @ngdoc method
     * @name vTvPackageServices.TvPackageSelectionStateService#hasChanged
     * @methodOf vTvPackageServices.TvPackageSelectionStateService
     * @returns {boolean} True if the active cart was changed and not processed
     */
    this.hasChanged = function () {
        return hasCartChanged;
    };

    /**
     * @ngdoc method
     * @name vTvPackageServices.TvPackageSelectionStateService#getPackageId
     * @methodOf vTvPackageServices.TvPackageSelectionStateService
     * @returns {string} The id of the currently active package
     */
    this.getPackageId = function () {
        return selectedPackageId;
    };

    /**
     * @ngdoc method
     * @name vTvPackageServices.TvPackageSelectionStateService#getDomain
     * @methodOf vTvPackageServices.TvPackageSelectionStateService
     * @returns {string} The domain were the packages comes from: "residential" or "business"
     */
    this.getDomain = function () {
        return domainName;
    };

    /**
     * @ngdoc method
     * @name vTvPackageServices.TvPackageSelectionStateService#isCategoryAvailable
     * @methodOf vTvPackageServices.TvPackageSelectionStateService
     * @param {string|number} catId The id of the Category
     * @returns {boolean} true if the category is enabled based on the selection state.
     */
    this.isCategoryAvailable = function (catId) {
        return _this.isALaCarte() || (categoryAvailability && catId in categoryAvailability ? !!categoryAvailability[catId] : false);
    };
});
/* eslint-enable max-statements */